import { useEffect, useRef, useCallback } from "react";

const BASE_INTERVAL = 60_000;   // 60s
const MAX_INTERVAL = 240_000;   // 4 min

/**
 * Adaptive state polling hook.
 *
 * - Polls with setTimeout (not setInterval) so the interval can grow.
 * - Doubles the interval on each 304 / error, resets on actual change.
 * - Pauses when the tab is hidden; does an immediate refresh when it becomes visible again.
 *
 * @param {string|null} projectId
 * @param {(id: string) => Promise<{updated: boolean, state?: any}>} refreshFn
 * @param {(state: any) => void} setState
 */
export function useStatePolling(projectId, refreshFn, setState) {
  const intervalRef = useRef(BASE_INTERVAL);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  // Keep latest setState/refreshFn in a ref so the effect doesn't re-run on every render.
  const callbacksRef = useRef({ refreshFn, setState });
  callbacksRef.current = { refreshFn, setState };

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!projectId) return;
    mountedRef.current = true;
    intervalRef.current = BASE_INTERVAL;

    async function poll() {
      if (!mountedRef.current) return;

      try {
        const r = await callbacksRef.current.refreshFn(projectId);
        if (!mountedRef.current) return;

        if (r.updated && r.state) {
          callbacksRef.current.setState(r.state);
          intervalRef.current = BASE_INTERVAL;
        } else {
          intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL);
        }
      } catch (e) {
        console.error("Poll failed:", e);
        intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL);
      }

      if (mountedRef.current) {
        timerRef.current = setTimeout(poll, intervalRef.current);
      }
    }

    // Start first poll after BASE_INTERVAL.
    timerRef.current = setTimeout(poll, intervalRef.current);

    function handleVisibility() {
      if (document.visibilityState === "visible") {
        // Tab became visible → immediate poll, reset interval.
        clearTimer();
        intervalRef.current = BASE_INTERVAL;
        poll();
      } else {
        // Tab hidden → pause polling.
        clearTimer();
      }
    }

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      mountedRef.current = false;
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [projectId, clearTimer]);
}
